/**
 * OwnerDashboard — Hidden "/owner" page surfacing platform-wide analytics
 * for whitelisted owner accounts.
 *
 * Gated server-side via `OWNER_EMAILS` env var. Frontend just calls
 * `/api/owner/stats` and renders. Non-owners get a 401/403 → bounced
 * back to /dashboard with a toast.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ElpmLogo from "@/components/ElpmLogo";
import axios from "axios";
import { motion } from "framer-motion";
import OwnerHelpBotModal from "@/components/owner/OwnerHelpBotModal";
import OwnerErrorsPanel from "@/components/owner/OwnerErrorsPanel";
import {
  Crown, Music, Mic2, Share2, Headphones, Sparkles, DollarSign,
  PlayCircle, Disc3, ArrowLeft, RefreshCw, Library, FileText,
} from "lucide-react";
import { toast } from "sonner";
import useAuth from "@/lib/useAuth";
import { safeApiError } from "@/lib/safeErrorText";

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, label, value, sub, tint = "fuchsia", testid }) {
  const tints = {
    fuchsia: "from-fuchsia-500/15 to-fuchsia-500/[0.03] border-fuchsia-400/30 text-fuchsia-200",
    amber: "from-amber-500/15 to-amber-500/[0.03] border-amber-400/30 text-amber-200",
    emerald: "from-emerald-500/15 to-emerald-500/[0.03] border-emerald-400/30 text-emerald-200",
    sky: "from-sky-500/15 to-sky-500/[0.03] border-sky-400/30 text-sky-200",
    rose: "from-rose-500/15 to-rose-500/[0.03] border-rose-400/30 text-rose-200",
  };
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br p-5 backdrop-blur-xl ${tints[tint] || tints.fuchsia}`}
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</span>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <p className="mt-3 font-display text-3xl font-semibold text-white">{value}</p>
      {sub && <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-white/45">{sub}</p>}
    </motion.div>
  );
}

function formatNumber(n) {
  if (n == null) return "—";
  if (typeof n !== "number") return String(n);
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString();
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function OwnerDashboard() {
  const { user, plan, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // iter-95 — Owner Help Bot modal (grant credits / rank / co-owner by chat).
  const [helpBotOpen, setHelpBotOpen] = useState(false);
  // iter-111 — System smoke test. Probes every external integration in
  // parallel and surfaces pass/fail right inside the Owner dashboard.
  const [smoke, setSmoke] = useState(null);
  const [smokeBusy, setSmokeBusy] = useState(false);

  const runSmokeTest = async () => {
    setSmokeBusy(true);
    try {
      const r = await axios.get(`${API}/api/owner/smoke_test`, { withCredentials: true });
      setSmoke(r.data);
      const failed = r.data?.summary?.failed ?? 0;
      if (failed === 0) {
        toast.success(`All systems green · ${r.data.summary.ok}/${r.data.summary.total} integrations OK`);
      } else {
        toast.warning(`${failed} integration${failed === 1 ? "" : "s"} need${failed === 1 ? "s" : ""} attention`, {
          description: r.data.results.filter((x) => !x.ok && !x.skipped).map((x) => x.label).join(", "),
          duration: 8000,
        });
      }
    } catch (e) {
      toast.error("Smoke test failed to launch", { description: safeApiError(e) });
    } finally {
      setSmokeBusy(false);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/api/owner/stats`, { withCredentials: true });
      setStats(r.data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        toast.error("Sign in required to view the owner dashboard");
        navigate("/dashboard");
        return;
      }
      if (status === 403) {
        toast.error("This dashboard is owner-only");
        navigate("/dashboard");
        return;
      }
      setError(safeApiError(e) || "Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      toast.error("Sign in required");
      navigate("/dashboard");
      return;
    }
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const isOwner = Boolean(plan?.is_owner);

  const libraryKindRows = useMemo(() => {
    if (!stats?.library?.by_kind) return [];
    const order = ["original", "extended", "cover", "wav", "video", "add_vocals", "add_instrumental", "separate_vocals"];
    return order
      .filter((k) => (stats.library.by_kind[k] || 0) > 0)
      .map((k) => ({ kind: k, count: stats.library.by_kind[k] }));
  }, [stats]);

  if (authLoading || (loading && !stats)) {
    return (
      <div className="min-h-screen bg-[#0a0814] p-8">
        <div className="mx-auto max-w-7xl">
          <div className="animate-pulse space-y-4">
            <div className="h-10 w-80 rounded-xl bg-white/5" />
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-32 rounded-2xl bg-white/5" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#0a0814] p-8">
        <div className="text-center">
          <p className="font-display text-2xl text-rose-200">Could not load dashboard</p>
          <p className="mt-2 font-mono text-sm text-white/55">{error}</p>
          <button
            data-testid="owner-retry-btn"
            onClick={fetchStats}
            className="mt-6 rounded-full border border-fuchsia-400/40 bg-fuchsia-500/15 px-5 py-2 font-mono text-xs uppercase tracking-wider text-fuchsia-100 hover:bg-fuchsia-500/25"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const { library, shares, top_shares, top_genres = [], counts, suno, payments, owner } = stats;

  return (
    <div
      data-testid="owner-dashboard"
      className="min-h-screen bg-[#0a0814] bg-[radial-gradient(ellipse_at_top,rgba(217,70,239,0.15),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(245,158,11,0.08),transparent_50%)] text-white"
    >
      <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              data-testid="owner-back-btn"
              onClick={() => navigate("/dashboard")}
              className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white/70 transition hover:border-white/25 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <ElpmLogo className="h-10" testId="owner-logo" />
            <div>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-fuchsia-400/45 bg-gradient-to-r from-fuchsia-500/25 to-amber-500/25 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white ring-1 ring-fuchsia-300/40">
                  <Crown className="h-3.5 w-3.5" /> Owner Console
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
                  Refreshed {new Date(stats.fetched_at).toLocaleTimeString()}
                </span>
              </div>
              <h1 className="mt-2 font-display text-3xl font-semibold leading-tight">
                Welcome back, <span className="bg-gradient-to-r from-fuchsia-200 to-amber-200 bg-clip-text text-transparent">{owner?.name?.split(" ")[0] || "Owner"}</span>
              </h1>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-white/45">
                {owner?.email} · member since {formatDate(owner?.since)}
              </p>
            </div>
          </div>
          <button
            data-testid="owner-refresh-btn"
            onClick={fetchStats}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full border border-fuchsia-400/40 bg-fuchsia-500/[0.12] px-4 py-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-fuchsia-100 transition hover:border-fuchsia-400/65 hover:bg-fuchsia-500/[0.22] disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            data-testid="owner-help-bot-open"
            onClick={() => setHelpBotOpen(true)}
            className="inline-flex items-center gap-2 rounded-full border border-amber-400/45 bg-gradient-to-br from-amber-500/25 to-fuchsia-500/15 px-4 py-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-amber-100 transition hover:from-amber-500/40 hover:to-fuchsia-500/25"
            title="Grant credits, ranks, co-ownership by chat"
          >
            <Crown className="h-3.5 w-3.5" />
            Help Bot
          </button>
        </div>

        {/* Headline cards */}
        <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            testid="owner-stat-library"
            icon={Library}
            label="My library"
            value={formatNumber(library.total)}
            sub={`${library.audio_count} audio · ${library.video_count} video`}
            tint="fuchsia"
          />
          <StatCard
            testid="owner-stat-shares"
            icon={Share2}
            label="Public shares"
            value={formatNumber(shares.total)}
            sub={`${formatNumber(shares.total_plays)} total plays`}
            tint="amber"
          />
          <StatCard
            testid="owner-stat-suno"
            icon={Sparkles}
            label="ELPM credits"
            value={suno?.available != null ? formatNumber(suno.available) : "—"}
            sub={suno?.error ? suno.error : "Songs available"}
            tint="emerald"
          />
          <StatCard
            testid="owner-stat-revenue"
            icon={DollarSign}
            label="Gross 30d"
            value={`$${(payments?.gross_usd_30d ?? 0).toFixed(2)}`}
            sub={`Lifetime $${(payments?.gross_usd ?? 0).toFixed(2)} · ${payments?.transactions_paid || 0} paid`}
            tint="sky"
          />
        </section>

        {/* Secondary counts */}
        <section className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            testid="owner-stat-recordings"
            icon={Mic2}
            label="Recordings"
            value={formatNumber(counts?.recording_sessions || 0)}
            tint="rose"
          />
          <StatCard
            testid="owner-stat-lyrics"
            icon={FileText}
            label="Lyrics drafts"
            value={formatNumber(counts?.lyrics_drafts || 0)}
            tint="fuchsia"
          />
          <StatCard
            testid="owner-stat-voices"
            icon={Headphones}
            label="Voice models"
            value={formatNumber(counts?.voice_models || 0)}
            tint="amber"
          />
          <StatCard
            testid="owner-stat-suno-tasks"
            icon={Disc3}
            label="ELPM tasks"
            value={formatNumber(counts?.suno_tasks || 0)}
            sub={`${counts?.plugin_presets || 0} plugin presets`}
            tint="emerald"
          />
        </section>

        {/* iter-111 — System smoke-test widget. One click pings every
            external integration in parallel + reports health. Run before
            every deploy. */}
        <section data-testid="owner-section-smoke" className="mt-10 rounded-3xl border border-white/10 bg-white/[0.04] p-5">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="font-display text-base font-bold tracking-tight text-white">System health</h2>
              <p className="font-mono text-[10px] uppercase tracking-wider text-white/45">
                Probe MongoDB · Emergent LLM · ELPM music engine · ElevenLabs · fal.ai · Stripe · Disk
              </p>
            </div>
            <button
              type="button"
              data-testid="owner-smoke-run"
              onClick={runSmokeTest}
              disabled={smokeBusy}
              className="flex items-center gap-1.5 rounded-xl border border-emerald-400/40 bg-gradient-to-r from-emerald-500/15 to-fuchsia-500/15 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-emerald-100 transition hover:from-emerald-500/25 hover:to-fuchsia-500/25 disabled:opacity-40"
            >
              <RefreshCw className={`h-3 w-3 ${smokeBusy ? "animate-spin" : ""}`} />
              {smokeBusy ? "Running…" : (smoke ? "Re-run" : "Test all systems")}
            </button>
          </div>
          {smoke ? (
            <>
              <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-white/55">
                <span className={smoke.summary.failed === 0 ? "text-emerald-300" : "text-rose-300"}>
                  {smoke.summary.ok}/{smoke.summary.total} OK
                </span>
                <span className="mx-2 text-white/25">·</span>
                <span className="text-white/45">checked {new Date(smoke.fetched_at).toLocaleTimeString()}</span>
              </div>
              <ul className="grid gap-2 md:grid-cols-2">
                {smoke.results.map((r) => (
                  <li
                    key={r.id}
                    data-testid={`owner-smoke-row-${r.id}`}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${
                      r.skipped
                        ? "border-amber-400/25 bg-amber-500/[0.05]"
                        : r.ok
                          ? "border-emerald-400/25 bg-emerald-500/[0.05]"
                          : "border-rose-400/30 bg-rose-500/[0.06]"
                    }`}
                  >
                    <span
                      className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                      style={{
                        backgroundColor: r.skipped ? "#f59e0b" : r.ok ? "#10b981" : "#f43f5e",
                        boxShadow: `0 0 8px ${r.skipped ? "#f59e0b80" : r.ok ? "#10b98180" : "#f43f5e80"}`,
                      }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-white">{r.label}</p>
                      <p className="truncate font-mono text-[10px] uppercase tracking-wider text-white/55">{r.detail}</p>
                    </div>
                    <span className="flex-shrink-0 font-mono text-[10px] tabular-nums text-white/45">
                      {r.latency_ms} ms
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="font-mono text-[11px] uppercase tracking-wider text-white/45">
              Click "Test all systems" — runs ~7 probes in parallel · finishes in 5-10 s
            </p>
          )}
        </section>

        {/* Library breakdown by kind */}
        {libraryKindRows.length > 0 && (
          <section data-testid="owner-section-kinds" className="mt-10">
            <h2 className="mb-4 font-display text-lg font-semibold">Library breakdown</h2>
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-2 backdrop-blur-xl">
              {libraryKindRows.map((row) => {
                const pct = library.total > 0 ? (row.count / library.total) * 100 : 0;
                return (
                  <div
                    key={row.kind}
                    data-testid={`owner-kind-${row.kind}`}
                    className="flex items-center gap-4 rounded-xl px-4 py-3 transition hover:bg-white/[0.03]"
                  >
                    <span className="w-32 font-mono text-[11px] uppercase tracking-wider text-white/65">
                      {row.kind.replace(/_/g, " ")}
                    </span>
                    <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-white/[0.04]">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-fuchsia-400 to-amber-400"
                        style={{ width: `${Math.max(2, pct)}%` }}
                      />
                    </div>
                    <span className="w-16 text-right font-mono text-xs text-white/80">{row.count}</span>
                    <span className="w-12 text-right font-mono text-[10px] text-white/40">{pct.toFixed(0)}%</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* iter-186 — Top genres widget. Aggregates library_songs by the
            first segment of the `style` field so the owner can see which
            genres they (or their org) generate most. */}
        {top_genres.length > 0 && (
          <section data-testid="owner-section-top-genres" className="mt-10">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold">Top genres</h2>
              <span className="font-mono text-[10px] uppercase tracking-wider text-white/40">
                Your library · top 5 · derived from style field
              </span>
            </div>
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-2 backdrop-blur-xl">
              {top_genres.map((row, idx) => {
                const max = top_genres[0]?.count || 1;
                const pct = (row.count / max) * 100;
                return (
                  <div
                    key={row.genre}
                    data-testid={`owner-top-genre-${idx}`}
                    className="flex items-center gap-4 rounded-xl px-4 py-3 transition hover:bg-white/[0.03]"
                  >
                    <span className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full bg-gradient-to-br from-fuchsia-500/30 to-amber-500/30 font-mono text-[11px] font-semibold text-white ring-1 ring-white/10">
                      {idx + 1}
                    </span>
                    <span className="w-48 truncate font-display text-sm text-white" title={row.genre}>
                      {row.genre}
                    </span>
                    <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-white/[0.04]">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-fuchsia-400 to-amber-400"
                        style={{ width: `${Math.max(4, pct)}%` }}
                      />
                    </div>
                    <span className="w-16 text-right font-mono text-xs text-white/80">{row.count}</span>
                    <span className="w-16 text-right font-mono text-[10px] uppercase tracking-wider text-white/40">
                      {row.count === 1 ? "song" : "songs"}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Top shares leaderboard */}
        <section data-testid="owner-section-top-shares" className="mt-10">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">Top played shares</h2>
            <span className="font-mono text-[10px] uppercase tracking-wider text-white/40">
              Platform-wide · top 10
            </span>
          </div>
          {top_shares.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center">
              <Music className="mx-auto h-6 w-6 text-white/30" />
              <p className="mt-3 font-mono text-[11px] uppercase tracking-wider text-white/40">
                No public shares yet
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {top_shares.map((s, i) => (
                <a
                  key={s.id}
                  href={`/song/${s.id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`owner-top-share-${i}`}
                  className="group flex items-center gap-4 rounded-xl border border-white/[0.05] bg-white/[0.02] p-4 transition hover:border-fuchsia-400/40 hover:bg-fuchsia-500/[0.05]"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-fuchsia-500/30 to-amber-500/30 font-display text-sm font-semibold text-white ring-1 ring-white/10">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display text-sm text-white group-hover:text-fuchsia-100">
                      {s.title || "Untitled"}
                    </p>
                    <p className="mt-0.5 truncate font-mono text-[10px] uppercase tracking-wider text-white/45">
                      {s.owner_alias || "anonymous"} · {s.model || "—"} · {formatDate(s.created_at)}
                    </p>
                  </div>
                  {s.media_type === "video" && (
                    <span className="rounded-full bg-amber-500/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-amber-200 ring-1 ring-amber-300/30">
                      Video
                    </span>
                  )}
                  <div className="flex items-center gap-2 text-fuchsia-200">
                    <PlayCircle className="h-4 w-4" />
                    <span className="font-mono text-sm">{formatNumber(s.plays)}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </section>

        {/* iter-96 — Live Inbox + Audit Log + Support Tickets */}
        <OwnerInboxAndAuditPanel />

        {/* iter-155 — Frontend crash reports from the ErrorBoundary */}
        <OwnerErrorsPanel />

        {/* Owner badge banner footer */}
        <div className="mt-12 rounded-2xl border border-fuchsia-400/30 bg-gradient-to-r from-fuchsia-500/[0.08] via-rose-500/[0.05] to-amber-500/[0.08] p-6 text-center">
          <Crown className="mx-auto h-6 w-6 text-fuchsia-300" />
          <p className="mt-3 font-display text-base text-white">
            All features unlocked · No quotas · No expiry
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
            Owner access valid until 2099 · {isOwner ? "Active" : "Verifying"}
          </p>
        </div>
      </div>
      <OwnerHelpBotModal open={helpBotOpen} onClose={() => setHelpBotOpen(false)} />
    </div>
  );
}


/** iter-96 — Three-tab live panel: signups feed, owner-bot audit, and
 *  support tickets the user-facing bot has escalated. */
function OwnerInboxAndAuditPanel() {
  const [tab, setTab] = useState("inbox"); // inbox | audit | tickets
  const [inbox, setInbox] = useState(null);
  const [audit, setAudit] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [i, a, t] = await Promise.allSettled([
        axios.get(`${API}/api/owner/inbox`, { withCredentials: true }),
        axios.get(`${API}/api/owner/audit`, { withCredentials: true }),
        axios.get(`${API}/api/support/tickets`, { withCredentials: true }),
      ]);
      if (i.status === "fulfilled") setInbox(i.value.data);
      if (a.status === "fulfilled") setAudit(a.value.data);
      if (t.status === "fulfilled") setTickets(t.value.data);
    } catch (e) { void e; }
    finally { setLoading(false); }
  };
  useEffect(() => {
    fetchAll();
    // Poll every 30 s so the inbox feels live without being expensive.
    const id = setInterval(fetchAll, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabBtnClass = (id) =>
    `rounded-xl px-3 py-2 font-mono text-[10px] uppercase tracking-wider transition ${
      tab === id
        ? "bg-fuchsia-500/22 text-fuchsia-100 ring-1 ring-fuchsia-400/40"
        : "bg-white/[0.04] text-white/65 ring-1 ring-white/10 hover:bg-white/[0.08]"
    }`;

  const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(); } catch (e) { void e; return iso; }
  };

  return (
    <section className="mt-10 rounded-3xl border border-white/10 bg-white/[0.04] p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-base font-bold tracking-tight text-white">Live Inbox</h2>
          <p className="text-[11px] text-white/55">New signups · public shares · audit log · open tickets — polls every 30 s.</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setTab("inbox")} className={tabBtnClass("inbox")} data-testid="owner-tab-inbox">
            Inbox {inbox?.signups?.length ? `(${inbox.signups.length})` : ""}
          </button>
          <button onClick={() => setTab("audit")} className={tabBtnClass("audit")} data-testid="owner-tab-audit">
            Audit {audit?.count ? `(${audit.count})` : ""}
          </button>
          <button onClick={() => setTab("tickets")} className={tabBtnClass("tickets")} data-testid="owner-tab-tickets">
            Tickets {tickets?.count ? `(${tickets.count})` : ""}
          </button>
          <button onClick={fetchAll} disabled={loading} className="flex items-center gap-1 rounded-xl bg-white/[0.06] px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-white/65 ring-1 ring-white/10 transition hover:bg-white/[0.10] disabled:opacity-50">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {tab === "inbox" && (
        <div className="space-y-4" data-testid="owner-inbox-panel">
          <div>
            <h3 className="mb-2 font-mono text-[10px] uppercase tracking-wider text-white/55">Latest signups</h3>
            {(inbox?.signups || []).length === 0 ? (
              <p className="text-sm text-white/45">No signups yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {inbox.signups.map((u) => (
                  <li key={u.user_id} className="flex items-center gap-3 rounded-xl bg-white/[0.04] px-3 py-2">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="h-7 w-7 rounded-full" />
                    ) : (
                      <div className="grid h-7 w-7 place-items-center rounded-full bg-fuchsia-500/15 font-mono text-xs text-fuchsia-200">{(u.name || u.email || "?")[0].toUpperCase()}</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-white">{u.name || u.email}</p>
                      <p className="truncate text-[11px] text-white/45">{u.email}</p>
                    </div>
                    {u.is_co_owner && <span className="rounded-full bg-amber-500/22 px-2 py-0.5 font-mono text-[9px] uppercase text-amber-200 ring-1 ring-amber-400/30">Co-owner</span>}
                    {u.plan && u.plan !== "free" && <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 font-mono text-[9px] uppercase text-emerald-200 ring-1 ring-emerald-400/30">{u.plan}</span>}
                    {u.rank && <span className="rounded-full bg-fuchsia-500/18 px-2 py-0.5 font-mono text-[9px] uppercase text-fuchsia-200 ring-1 ring-fuchsia-400/30">{u.rank}</span>}
                    <span className="font-mono text-[10px] text-white/40">{fmt(u.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h3 className="mb-2 font-mono text-[10px] uppercase tracking-wider text-white/55">Latest public shares</h3>
            {(inbox?.shares || []).length === 0 ? (
              <p className="text-sm text-white/45">No shares yet.</p>
            ) : (
              <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {inbox.shares.map((s) => (
                  <li key={s.share_id} className="flex items-center gap-3 rounded-xl bg-white/[0.04] px-3 py-2">
                    {s.thumbnail_url ? (
                      <img src={s.thumbnail_url} alt="" className="h-9 w-9 rounded-lg object-cover" />
                    ) : (
                      <div className="grid h-9 w-9 place-items-center rounded-lg bg-fuchsia-500/14 text-fuchsia-200"><Disc3 className="h-4 w-4" /></div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-white">{s.title}</p>
                      <p className="truncate text-[10px] text-white/45">{s.owner_email}</p>
                    </div>
                    <span className="font-mono text-[10px] text-white/60">{s.view_count}v</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {!!(inbox?.rank_changes || []).length && (
            <div>
              <h3 className="mb-2 font-mono text-[10px] uppercase tracking-wider text-white/55">Recent rank changes (7d)</h3>
              <ul className="space-y-1">
                {inbox.rank_changes.map((r, idx) => (
                  <li key={`${r.ts || idx}-${r.summary?.slice(0, 40) || ""}`} className="rounded-xl bg-white/[0.04] px-3 py-2 text-[12.5px] text-white/85">
                    {r.summary} <span className="font-mono text-[10px] text-white/40">{fmt(r.ts)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === "audit" && (
        <div data-testid="owner-audit-panel">
          {(audit?.rows || []).length === 0 ? (
            <p className="text-sm text-white/45">No actions logged yet.</p>
          ) : (
            <ul className="space-y-1">
              {audit.rows.map((row) => (
                <li key={row.id} className="rounded-xl bg-white/[0.04] px-3 py-2 text-[12.5px] text-white/85">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-fuchsia-200/85">{(row.action || "").replace(/_/g, " ")}</span>
                  <span className="ml-2">{row.summary}</span>
                  <span className="ml-2 font-mono text-[10px] text-white/40">{fmt(row.ts)} · by {row.owner_email || "owner"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "tickets" && (
        <div data-testid="owner-tickets-panel">
          {(tickets?.rows || []).length === 0 ? (
            <p className="text-sm text-white/45">No open tickets — everyone seems happy.</p>
          ) : (
            <ul className="space-y-1.5">
              {tickets.rows.map((t) => (
                <li key={t.ticket_id} className="rounded-xl bg-white/[0.04] px-3 py-2 text-[12.5px] text-white/85">
                  <div className="flex items-start justify-between gap-2">
                    <p className="flex-1 break-words"><span className="font-mono text-[10px] uppercase text-amber-200/85">{t.status || "open"}</span> · {t.message}</p>
                    <span className="font-mono text-[10px] text-white/40">{fmt(t.created_at)}</span>
                  </div>
                  {t.context?.page && (
                    <p className="mt-0.5 font-mono text-[10px] text-white/40">page: {t.context.page}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
